import { useState, useEffect, useCallback } from 'react'
import { TrendingUp } from 'lucide-react'
import { SkeletonChart, SkeletonCard } from '@/components/ui/Skeleton'
import {
  Area, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ComposedChart, Legend,
} from 'recharts'

interface EventRow {
  event_id: number
  event_name: string
  start_date: string
  revenue: number
  guests: number
  total_scans: number
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`
  return `$${n.toLocaleString()}`
}

function fmtNum(n: number): string {
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

const years = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() - i).toString())

export function CompanyTrends() {
  const [yearlyData, setYearlyData] = useState<{ year: string; revenue: number; guests: number; events: number }[]>([])
  const [loading, setLoading] = useState(false)

  const fetchAllYears = useCallback(async () => {
    setLoading(true)
    const settled = await Promise.allSettled(
      years.map(async (y) => {
        const res = await fetch(`/api/dashboard/season?year=${y}`, { credentials: 'same-origin' })
        if (!res.ok) return { year: y, revenue: 0, guests: 0, events: 0 }
        const d = await res.json()
        return {
          year: y,
          revenue: d.metrics?.revenue_total ?? d.metrics?.revenue ?? 0,
          guests: d.metrics?.guests ?? 0,
          events: d.events_with_data ?? 0,
        }
      })
    )
    const results = settled.map((r, i) =>
      r.status === 'fulfilled' ? r.value : { year: years[i], revenue: 0, guests: 0, events: 0 }
    )
    setYearlyData(results.reverse())
    setLoading(false)
  }, [])

  useEffect(() => { fetchAllYears() }, [fetchAllYears])

  const [monthlyData, setMonthlyData] = useState<EventRow[]>([])

  useEffect(() => {
    const year = new Date().getFullYear().toString()
    fetch(`/api/dashboard/season?year=${year}`, { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.per_event) setMonthlyData(d.per_event)
      })
      .catch(() => {})
  }, [])

  const monthlyAgg = monthlyData.reduce<Record<string, { revenue: number; guests: number; count: number }>>((acc, ev) => {
    const month = ev.start_date?.slice(0, 7) || 'unknown'
    if (!acc[month]) acc[month] = { revenue: 0, guests: 0, count: 0 }
    acc[month].revenue += ev.revenue
    acc[month].guests += ev.guests
    acc[month].count += 1
    return acc
  }, {})

  const monthlyChart = Object.entries(monthlyAgg)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, d]) => ({
      month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short' }),
      revenue: d.revenue,
      guests: d.guests,
      events: d.count,
    }))

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-butler-50">
          <TrendingUp className="h-5 w-5 text-butler-600" />
        </div>
        <div>
          <h1 className="font-display font-bold text-xl text-ink-dark">Company Trends</h1>
          <p className="text-sm text-ink-soft">Year-over-year and monthly performance trends</p>
        </div>
      </div>

      {loading && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4"><SkeletonCard /><SkeletonCard /><SkeletonCard /></div>
          <SkeletonChart />
        </div>
      )}

      {!loading && yearlyData.length > 0 && (
        <>
          {/* Year-over-Year Revenue + Guests */}
          <section className="mm-card-v3 p-5">
            <h2 className="font-display font-semibold text-sm text-ink-dark mb-4">Year-over-Year Revenue &amp; Guests</h2>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={yearlyData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#8697A8' }} />
                  <YAxis yAxisId="rev" tick={{ fontSize: 11, fill: '#8697A8' }} tickFormatter={(v: number) => fmt(v).replace('$', '')} />
                  <YAxis yAxisId="guests" orientation="right" tick={{ fontSize: 11, fill: '#8697A8' }} tickFormatter={(v: number) => fmtNum(v)} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                    formatter={(value: number, name: string) =>
                      name === 'revenue' ? [fmt(value), 'Revenue'] : [fmtNum(value), 'Guests']
                    }
                  />
                  <Legend />
                  <Area yAxisId="rev" dataKey="revenue" fill="#0070ab" fillOpacity={0.1} stroke="#0070ab" strokeWidth={2} />
                  <Line yAxisId="guests" dataKey="guests" stroke="#00c7ca" strokeWidth={2} dot={{ fill: '#00c7ca', r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* Yearly summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {yearlyData.map(y => (
              <div key={y.year} className="mm-card-v3 p-3 text-center">
                <div className="mm-data text-lg font-bold text-ink-dark">{y.year}</div>
                <div className="text-sm font-semibold text-butler-600 mt-1">{fmt(y.revenue)}</div>
                <div className="text-[10px] text-ink-soft">{fmtNum(y.guests)} guests · {y.events} events</div>
              </div>
            ))}
          </div>

          {/* Monthly revenue for current year */}
          {monthlyChart.length > 0 && (
            <section className="mm-card-v3 p-5">
              <h2 className="font-display font-semibold text-sm text-ink-dark mb-4">Monthly Revenue ({new Date().getFullYear()})</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={monthlyChart} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#8697A8' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#8697A8' }} tickFormatter={(v: number) => fmt(v).replace('$', '')} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                      formatter={(value: number, name: string) =>
                        name === 'revenue' ? [fmt(value), 'Revenue'] : [String(value), name]
                      }
                    />
                    <Area dataKey="revenue" fill="#0070ab" fillOpacity={0.15} stroke="#0070ab" strokeWidth={2} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}
